import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

export type Categoria = Database['public']['Tables']['categorias']['Row'];

export interface EmpresaCardData {
  id: string;
  nome: string;
  logo_url: string | null;
  descricao_publica: string | null;
  rating_media: number;
  rating_total: number;
  categorias: { slug: string; nome: string }[];
  cidades: string[];
}

export interface EmpresaPublicaData extends EmpresaCardData {
  servicos: string[];
}

export interface CidadeMarketplace {
  cidade_norm: string;
  cidade: string;
}

export interface FiltrosDiretorio {
  categoriaSlug?: string | null;
  cidadeNorm?: string | null;
}

/** Categorias ativas (lista do filtro). */
export async function listarCategorias(): Promise<Categoria[]> {
  const { data, error } = await supabase
    .from('categorias')
    .select('*')
    .eq('ativo', true)
    .order('ordem');
  if (error) throw error;
  return data ?? [];
}

/** Empresas visíveis (RPC pública, gate Professional+), filtros opcionais. */
export async function listarEmpresasMarketplace(f: FiltrosDiretorio): Promise<EmpresaCardData[]> {
  const { data, error } = await supabase.rpc('marketplace_listar_empresas', {
    p_categoria_slug: f.categoriaSlug ?? undefined,
    p_cidade_norm: f.cidadeNorm ?? undefined,
  });
  if (error) throw error;
  return (data as unknown as EmpresaCardData[]) ?? [];
}

/** Perfil público de uma empresa visível, ou null (RPC devolve SQL NULL se não visível). */
export async function obterEmpresaPublica(id: string): Promise<EmpresaPublicaData | null> {
  const { data, error } = await supabase.rpc('marketplace_obter_empresa', { p_id: id });
  if (error) throw error;
  return (data as unknown as EmpresaPublicaData) ?? null;
}

/** Cidades distintas servidas por empresas visíveis (dropdown). */
export async function listarCidadesMarketplace(): Promise<CidadeMarketplace[]> {
  const { data, error } = await supabase.rpc('marketplace_listar_cidades');
  if (error) throw error;
  return (data as unknown as CidadeMarketplace[]) ?? [];
}

/** Atualiza a descrição pública da própria org. */
export async function definirDescricaoPublica(descricao: string): Promise<void> {
  const { error } = await supabase.rpc('definir_descricao_publica', { p_descricao: descricao });
  if (error) throw error;
}

/** Categorias atuais da própria org (ids). */
export async function listarCategoriasOrg(orgId: string): Promise<string[]> {
  const { data, error } = await supabase.from('organization_categorias').select('categoria_id').eq('organization_id', orgId);
  if (error) throw error;
  return (data ?? []).map((r) => r.categoria_id);
}

/** Substitui (atomicamente, via RPC) o conjunto de categorias da org. */
export async function definirCategoriasOrg(_orgId: string, categoriaIds: string[]): Promise<void> {
  const { error } = await supabase.rpc('definir_categorias_org', { p_ids: categoriaIds });
  if (error) throw error;
}

/** Cidades atuais da própria org. */
export async function listarCidadesOrg(orgId: string): Promise<{ id: string; cidade: string }[]> {
  const { data, error } = await supabase.from('organization_cidades').select('id, cidade').eq('organization_id', orgId).order('cidade');
  if (error) throw error;
  return data ?? [];
}

/** Adiciona uma cidade (normaliza; ignora duplicado por unique 23505). */
export async function adicionarCidadeOrg(orgId: string, cidade: string, cidadeNorm: string): Promise<void> {
  const { error } = await supabase.from('organization_cidades').insert({ organization_id: orgId, cidade, cidade_norm: cidadeNorm });
  if (error && error.code !== '23505') throw error;
}

/** Remove uma cidade por id. */
export async function removerCidadeOrg(id: string): Promise<void> {
  const { error } = await supabase.from('organization_cidades').delete().eq('id', id);
  if (error) throw error;
}
